import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { useAuth } from "../context/connectionContext";
import { useUserData } from "../context/userContext";
import { useAppSelector } from "../hooks";
import { useNavigate, useLocation } from "react-router-dom";
import { selectSnapshotStatus } from "../state/snapshot/snapshotSlice";
import { capitalizeFirstChar } from "../utils/textUtils";
import { useMemo, useState, useCallback, memo, MouseEvent } from "react";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CampaignIcon from "@mui/icons-material/Campaign";
import UserMenu from "./appBar/UserMenu";
import PendingActionsButton from "../components/PendingActionsButton";

function ResponsiveAppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { disconnect, isConnected, connect } = useAuth();
  const { userSnapshot } = useUserData();
  const status = useAppSelector(selectSnapshotStatus);

  const pathname = location.pathname;
  const isManageSafeActive = pathname.includes("/manage-safe");
  const isPortfolioActive = pathname.includes("/portfolio");
  const isActionsActive = pathname.startsWith("/actions");

  // Memoize settings to prevent recreation on every render
  const settings = useMemo(
    () => [
      {
        label: "Manage Helpers",
        action: () => {
          navigate("/helpers");
        },
      },
      {
        label: "Manage Trustors",
        action: () => {
          navigate("/trustors");
        },
      },
      {
        label: "Logout",
        action: () => {
          disconnect();
          navigate("/");
        },
      },
    ],
    [disconnect, navigate]
  );

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  // Memoize event handlers
  const handleOpenNavMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  }, []);

  const handleCloseNavMenu = useCallback(() => {
    setAnchorElNav(null);
  }, []);

  const handleLogoClick = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleNavigateToManageSafe = useCallback(() => {
    navigate("/safes", { state: { nextPage: "/manage-safe" } });
    handleCloseNavMenu();
  }, [navigate, handleCloseNavMenu]);

  const handleNavigateToPortfolio = useCallback(() => {
    navigate("/safes", { state: { nextPage: "/portfolio" } });
    handleCloseNavMenu();
  }, [navigate, handleCloseNavMenu]);

  const handleNavigateToActions = useCallback(() => {
    navigate("/actions");
    handleCloseNavMenu();
  }, [navigate, handleCloseNavMenu]);

  // Memoize computed values
  const hasSafes = useMemo(
    () => isConnected && userSnapshot?.safes?.length > 0,
    [isConnected, userSnapshot?.safes?.length]
  );

  return (
    <AppBar position="sticky">
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{ position: "relative", width: "100%", minHeight: { xs: 56, md: 64 } }}
        >
          {/* Left: hamburger (small) or hamburger + logo (desktop) */}
          <Box
            sx={{
              display: "flex",
              flex: { xs: 1, md: "0 0 auto" },
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Box>
              <IconButton
                size="large"
                aria-label="Open navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
              >
                {hasSafes && (
                  <MenuItem onClick={handleNavigateToManageSafe} sx={{ minHeight: 44 }}>
                    <AdminPanelSettingsIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography
                      sx={{ textAlign: "center", fontWeight: isManageSafeActive ? 600 : 400 }}
                    >
                      {capitalizeFirstChar("Manage Safe")}
                    </Typography>
                  </MenuItem>
                )}
                {hasSafes && (
                  <MenuItem onClick={handleNavigateToPortfolio} sx={{ minHeight: 44 }}>
                    <AccountBalanceWalletIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography
                      sx={{ textAlign: "center", fontWeight: isPortfolioActive ? 600 : 400 }}
                    >
                      {capitalizeFirstChar("Portfolio")}
                    </Typography>
                  </MenuItem>
                )}
                {isConnected && (
                  <MenuItem onClick={handleNavigateToActions} sx={{ minHeight: 44 }}>
                    <CampaignIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography
                      sx={{ textAlign: "center", fontWeight: isActionsActive ? 600 : 400 }}
                    >
                      {capitalizeFirstChar("Actions Center")}
                    </Typography>
                  </MenuItem>
                )}
              </Menu>
            </Box>
            {/* Logo on desktop: next to hamburger */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <IconButton
                onClick={handleLogoClick}
                color="primary"
                size="large"
                sx={{
                  ml: 0.5,
                  "&:hover": { backgroundColor: "transparent" },
                  "&:active": { backgroundColor: "transparent" },
                  "&:focus": { outline: "none" },
                  "&:focus-visible": { outline: "none" },
                  "@media (hover: none)": { "&:hover": { backgroundColor: "transparent" } },
                }}
                disableRipple
              >
                <img
                  src="/assets/icons/wiKeyLogo1.png"
                  alt="wiKey"
                  style={{ width: 100, height: 60 }}
                />
              </IconButton>
            </Box>
          </Box>

          {/* Center (small screens only): logo centered */}
          <Box
            sx={{
              flex: { xs: 0, md: "0 0 auto" },
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              alignItems: "center",
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              pointerEvents: "none",
            }}
          >
            <Box sx={{ pointerEvents: "auto" }}>
              <IconButton
                onClick={handleLogoClick}
                color="primary"
                size="large"
                sx={{
                  "&:hover": { backgroundColor: "transparent" },
                  "&:active": { backgroundColor: "transparent" },
                  "&:focus": { outline: "none" },
                  "&:focus-visible": { outline: "none" },
                  "@media (hover: none)": { "&:hover": { backgroundColor: "transparent" } },
                }}
                disableRipple
              >
                <img
                  src="/assets/icons/wiKeyLogo1.png"
                  alt="wiKey"
                  style={{ width: 100, height: 60 }}
                />
              </IconButton>
            </Box>
          </Box>

          {/* Right: equal flex on small (for center logo), spacer + actions */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <PendingActionsButton />
            {isConnected ? (
              <UserMenu settings={settings} />
            ) : (
              <Button variant="contained" color="primary" onClick={() => connect()}>
                Connect
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default memo(ResponsiveAppBar);
